import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import { api, fmtEUR, fmtDate } from "@/lib/api";
import { useSuggestions, AutocompleteInput, refreshSuggestions } from "@/lib/suggestions";
import BackupReminder from "@/components/BackupReminder";
import PageHeader from "@/components/PageHeader";
import OnTheTable from "@/components/OnTheTable";
import CandidaturesList from "@/components/CandidaturesList";
import { PROJECT_TYPES, SERIES_TYPES, buildTypeOptions, resolveTypeChoice } from "@/lib/project_types";
import { PROJECT_PHASES, DEFAULT_PHASE, phaseStyle } from "@/lib/project_phases";

/**
 * Projects · Pizarra del catálogo (Mockup D #s1).
 *
 * Vista principal post-login. Estructura:
 *  · PageHeader con eyebrow + título + acciones (búsqueda + nuevo proyecto).
 *  · Pills de filtrado por fase con contadores.
 *  · Kanban horizontal: 6 columnas (Idea · Desarrollo · Financiación · Preprod
 *    · Producción · Distribución). Las tarjetas son draggables → al soltarlas
 *    en otra columna se actualiza `phase` del proyecto vía PUT optimista.
 *  · Cada tarjeta muestra: título, meta (tipo · duración), descripción
 *    opcional, barra de progreso de financiación, importe y % cobertura.
 *  · Fallback vista lista en mobile (sin Kanban; las tarjetas se apilan).
 *
 * El sidebar global está en `AppShell` (gestionado en App.js).
 */
export default function ProjectsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", project_type: PROJECT_TYPES[0], phase: DEFAULT_PHASE, director: "", producer: "", episodes: 0 });
  const [phaseFilter, setPhaseFilter] = useState("ALL");
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const sugg = useSuggestions();

  // Soporte para abrir el modal "+ Nuevo proyecto" vía ?new=1 (desde sidebar)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setCreating(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/icaa/projects");
      setRows(r.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Counts per phase
  const phaseCounts = useMemo(() => {
    const counts = { ALL: rows.length };
    PROJECT_PHASES.forEach((p) => { counts[p] = 0; });
    rows.forEach((r) => {
      const p = r.phase || DEFAULT_PHASE;
      counts[p] = (counts[p] || 0) + 1;
    });
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (phaseFilter === "ALL") return rows;
    return rows.filter((r) => (r.phase || DEFAULT_PHASE) === phaseFilter);
  }, [rows, phaseFilter]);

  const byPhase = useMemo(() => {
    const out = {};
    PROJECT_PHASES.forEach((p) => { out[p] = []; });
    filteredRows.forEach((r) => {
      const ph = PROJECT_PHASES.includes(r.phase) ? r.phase : DEFAULT_PHASE;
      out[ph].push(r);
    });
    return out;
  }, [filteredRows]);

  const stats = useMemo(() => {
    const total = filteredRows.length;
    const sumBudget = filteredRows.reduce((s, p) => s + (p.total || 0), 0);
    const sumFin = filteredRows.reduce((s, p) => s + (p.financing_total || 0), 0);
    const types = new Set(filteredRows.map((r) => r.project_type)).size;
    return { total, sumBudget, sumFin, types };
  }, [filteredRows]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const r = await api.post("/icaa/projects", form);
    refreshSuggestions();
    setCreating(false);
    setForm({ title: "", project_type: PROJECT_TYPES[0], phase: DEFAULT_PHASE, director: "", producer: "", episodes: 0 });
    nav(`/project/${r.data.id}`);
  };

  const remove = async (id, title) => {
    if (!window.confirm(`¿Eliminar "${title}"?`)) return;
    await api.delete(`/icaa/projects/${id}`);
    load();
  };

  const updatePhase = async (id, newPhase) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, phase: newPhase } : r)));
    try {
      await api.put(`/icaa/projects/${id}`, { phase: newPhase });
    } catch (err) {
      await load();
      alert("No se pudo cambiar la fase: " + (err?.response?.data?.detail || err.message));
    }
  };

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const projectId = String(active.id);
    const targetPhase = String(over.id);
    if (!PROJECT_PHASES.includes(targetPhase)) return;
    const proj = rows.find((r) => r.id === projectId);
    if (!proj) return;
    if ((proj.phase || DEFAULT_PHASE) === targetPhase) return;
    updatePhase(projectId, targetPhase);
  };

  return (
    <div className="min-h-screen bg-gb-bg text-gb-black">
      <BackupReminder />

      <PageHeader
        eyebrow="Catálogo"
        title="Tus historias en producción"
        subtitle={`${stats.total} proyectos · ${fmtEUR(stats.sumBudget)} agregado · ${fmtEUR(stats.sumFin)} captado`}
        actions={
          <>
            <button
              onClick={() => setCreating(true)}
              data-testid="new-project-btn"
              className="inline-flex items-center gap-2 rounded-full bg-gb-red px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover-gb-red"
            >
              + Nuevo proyecto
            </button>
          </>
        }
      />

      <section className="mx-auto max-w-[1480px] px-6 py-7 sm:px-10">
        {/* Phase tabs */}
        <div className="mb-5 -mx-1 flex items-center gap-1.5 overflow-x-auto px-1 pb-1 sm:mb-6" data-testid="phase-tabs">
          <span className="mr-1 hidden text-[10px] font-semibold uppercase tracking-[0.22em] text-gb-muted sm:inline">
            Filtrar por fase:
          </span>
          <PhaseTab label="Todos" count={phaseCounts.ALL} active={phaseFilter === "ALL"}
                    onClick={() => setPhaseFilter("ALL")} testid="phase-tab-ALL" />
          {PROJECT_PHASES.map((ph) => {
            const sty = phaseStyle(ph);
            return (
              <PhaseTab key={ph} label={sty.label} count={phaseCounts[ph] || 0}
                        active={phaseFilter === ph}
                        hex={sty.hex}
                        onClick={() => setPhaseFilter(ph)}
                        testid={`phase-tab-${ph}`} />
            );
          })}
        </div>

        {/* Kanban horizontal — desktop / tablet */}
        <div className="hidden md:block">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <div className="grid gap-3 lg:gap-4" style={{ gridTemplateColumns: `repeat(${PROJECT_PHASES.length}, minmax(220px, 1fr))` }}
                 data-testid="phase-kanban">
              {PROJECT_PHASES.map((ph) => (
                <PhaseLane key={ph} phase={ph} projects={byPhase[ph] || []}
                           onRemove={remove} loading={loading} />
              ))}
            </div>
          </DndContext>
          <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-gb-muted">
            Arrastra una tarjeta entre columnas para cambiar su fase.
          </p>
        </div>

        {/* Mobile fallback: stacked cards */}
        <div className="space-y-3 md:hidden" data-testid="projects-cards">
          {loading && (
            <p className="rounded-xl border border-gb-paper bg-white p-6 text-center text-sm text-gb-muted">
              Cargando proyectos…
            </p>
          )}
          {!loading && filteredRows.length === 0 && (
            <EmptyState filter={phaseFilter} />
          )}
          {filteredRows.map((p) => (
            <MobileCard key={p.id} project={p} onRemove={remove} onChangePhase={updatePhase} />
          ))}
        </div>

        {/* Qué hay sobre la mesa */}
        <OnTheTable projects={rows} />

        {/* Candidaturas activas (C1) */}
        <section className="mt-10">
          <CandidaturesList compact title="Candidaturas en curso" />
        </section>
      </section>

      {creating && (
        <NewProjectModal form={form} setForm={setForm} onSubmit={create}
                         onCancel={() => setCreating(false)} suggestions={sugg} />
      )}
    </div>
  );
}

// ─── Kanban lane ──────────────────────────────────────────────────────────

function PhaseLane({ phase, projects, onRemove, loading }) {
  const sty = phaseStyle(phase);
  const { setNodeRef, isOver } = useDroppable({ id: phase });
  return (
    <div
      ref={setNodeRef}
      data-testid={`lane-${phase}`}
      className={`flex flex-col rounded-2xl border bg-gb-paper-soft/40 transition ${
        isOver ? "border-gb-red bg-gb-red/5" : "border-gb-paper"
      }`}
    >
      <div className="flex items-center gap-2 border-b border-gb-paper px-3 py-2.5">
        <span className="inline-block h-2 w-2 rounded-full" style={{ background: sty.hex }} />
        <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-black">{sty.label}</span>
        <span className="ml-auto text-[11px] tabular-nums text-gb-muted">{projects.length}</span>
      </div>
      <div className="flex min-h-[280px] flex-1 flex-col gap-2 p-2.5">
        {loading && (
          <p className="rounded-md border border-dashed border-gb-paper p-3 text-center text-[11px] text-gb-muted">Cargando…</p>
        )}
        {!loading && projects.length === 0 && (
          <p className="rounded-md border border-dashed border-gb-paper p-3 text-center text-[11px] text-gb-muted">—</p>
        )}
        {projects.map((p) => (
          <DraggableCard key={p.id} project={p} onRemove={onRemove} accent={sty.hex} />
        ))}
      </div>
    </div>
  );
}

function DraggableCard({ project, onRemove, accent }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: project.id });
  const cover = project.total > 0 ? (project.financing_total / project.total) * 100 : 0;
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 }
    : undefined;
  return (
    <article
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid={`project-card-${project.id}`}
      className={`group relative cursor-grab rounded-lg border border-gb-paper bg-gb-surface p-3 text-left shadow-sm transition active:cursor-grabbing ${
        isDragging ? "opacity-50 shadow-xl" : "hover:-translate-y-0.5 hover:shadow-md"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <Link to={`/project/${project.id}`} className="min-w-0 flex-1" onPointerDown={(e) => e.stopPropagation()}
              data-testid={`open-project-${project.id}`}>
          <p className="truncate font-serif text-[15px] leading-tight text-gb-black group-hover:text-gb-red">
            {project.title}
          </p>
          <p className="mt-0.5 truncate text-[10px] uppercase tracking-[0.18em] text-gb-muted">
            {project.project_type}{project.duration_min ? ` · ${project.duration_min}'` : ""}
          </p>
        </Link>
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onRemove(project.id, project.title)}
          data-testid={`delete-project-${project.id}`}
          className="rounded-full p-1 text-gb-muted/60 transition hover:bg-gb-red/10 hover:text-gb-red"
          title="Eliminar"
        >×</button>
      </div>

      {project.director && (
        <p className="mt-1.5 truncate text-[11px] text-gb-muted">
          {project.director}
        </p>
      )}

      {project.total > 0 && (
        <div className="mt-2.5">
          <div className="h-1 overflow-hidden rounded-full bg-gb-paper">
            <div className={`h-full ${cover >= 100 ? "bg-emerald-600" : ""}`}
                 style={{ width: `${Math.min(100, cover)}%`, background: cover >= 100 ? "" : accent }} />
          </div>
        </div>
      )}

      <div className="mt-2.5 flex items-center justify-between text-[10px] uppercase tracking-[0.16em]">
        <span className="font-mono tabular-nums text-gb-black">{fmtEUR(project.total)}</span>
        <span className={cover >= 100 ? "text-emerald-700 font-semibold" : "text-gb-red font-semibold"}>
          {project.total > 0 ? `${cover.toFixed(0)}% cub.` : "—"}
        </span>
      </div>

      <div className="mt-1 text-[10px] text-gb-muted/70">
        {fmtDate(project.updated_at)}
      </div>
    </article>
  );
}

function MobileCard({ project, onRemove, onChangePhase }) {
  const cover = project.total > 0 ? (project.financing_total / project.total) * 100 : 0;
  return (
    <article className="rounded-xl border border-gb-paper bg-gb-surface p-4 shadow-sm" data-testid={`project-card-m-${project.id}`}>
      <div className="flex items-start justify-between gap-2">
        <Link to={`/project/${project.id}`} className="min-w-0 flex-1">
          <p className="truncate font-serif text-lg text-gb-black">{project.title}</p>
          <p className="mt-1 text-xs text-gb-muted">{project.project_type} · {project.director || "Director pendiente"}</p>
        </Link>
        <button onClick={() => onRemove(project.id, project.title)} data-testid={`delete-project-${project.id}-m`}
                className="rounded-full border border-gb-paper px-2 py-1 text-xs text-gb-muted hover:border-gb-red hover:text-gb-red">×</button>
      </div>
      <div className="mt-2">
        <PhasePill phase={project.phase || DEFAULT_PHASE}
                   onChange={(v) => onChangePhase(project.id, v)}
                   testid={`phase-pill-${project.id}-m`} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <div><span className="text-gb-muted">Presup.</span><br/><span className="font-semibold tabular-nums">{fmtEUR(project.total)}</span></div>
        <div>
          <span className="text-gb-muted">Financ.</span><br/>
          <span className="tabular-nums">{fmtEUR(project.financing_total)}
            <span className={`ml-1 text-[10px] ${cover >= 100 ? "text-emerald-700" : "text-gb-red"}`}>{cover.toFixed(0)}%</span>
          </span>
        </div>
      </div>
      <Link to={`/project/${project.id}`} data-testid={`open-project-${project.id}-m`}
            className="mt-3 block rounded-full bg-gb-black py-2 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-gb-red">
        Abrir proyecto
      </Link>
    </article>
  );
}

// ─── Auxiliares UI ────────────────────────────────────────────────────────

const EmptyState = ({ filter }) => (
  <div className="rounded-xl border border-gb-paper bg-gb-surface p-8 text-center">
    <p className="font-serif text-xl text-gb-black">
      {filter === "ALL" ? "Empieza por el primero" : "Sin proyectos en esta fase"}
    </p>
    <p className="mt-2 text-sm text-gb-muted">
      {filter === "ALL"
        ? "Crea un nuevo proyecto y la estructura ICAA + dossier se inicializa lista para rellenar."
        : "Arrastra un proyecto a esta columna o crea uno nuevo en esta fase."}
    </p>
  </div>
);

const PhaseTab = ({ label, count, active, hex, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex shrink-0 items-center gap-2 rounded-full px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition ${
      active
        ? "bg-gb-black text-white"
        : "border border-gb-paper bg-gb-surface text-gb-black/65 hover:border-gb-black/30 hover:text-gb-black"
    }`}
  >
    {hex && <span className="h-2 w-2 rounded-full" style={{ background: hex }} />}
    <span>{label}</span>
    <span className={`tabular-nums text-[10px] ${active ? "text-white/65" : "text-gb-muted"}`}>
      {count}
    </span>
  </button>
);

const PhasePill = ({ phase, onChange, testid }) => {
  const sty = phaseStyle(phase);
  return (
    <div className="relative inline-block">
      <select
        value={phase}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        title="Cambiar fase"
        className={`appearance-none rounded-full ${sty.bg} ${sty.text} cursor-pointer pl-6 pr-6 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] outline-none ring-0 hover:opacity-90 focus:ring-2 focus:ring-gb-red/30`}
      >
        {PROJECT_PHASES.map((p) => (
          <option key={p} value={p}>{phaseStyle(p).label}</option>
        ))}
      </select>
      <span className={`pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full ${sty.dot}`} />
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[8px] opacity-50">▾</span>
    </div>
  );
};

// ─── Modal: Nuevo proyecto ────────────────────────────────────────────────

const NewProjectModal = ({ form, setForm, onSubmit, onCancel, suggestions }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-gb-black/70 p-4 backdrop-blur"
    onClick={onCancel}
    data-testid="new-project-modal"
  >
    <form
      onClick={(e) => e.stopPropagation()}
      onSubmit={onSubmit}
      className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-2xl"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.4em] text-gb-red">
        grabarte · nuevo proyecto
      </p>
      <h2 className="mt-2 gb-display text-3xl">Empieza un proyecto audiovisual</h2>
      <p className="mt-2 text-sm text-gb-muted">
        Se inicializa con dossier vacío, los 12 capítulos del ICAA y plantilla de financiación.
      </p>
      <div className="mt-6 space-y-4">
        <Input label="Título" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="new-title" />
        <div className="grid grid-cols-2 gap-3">
          <Select label="Tipo" value={form.project_type}
                  onChange={(v) => {
                    const next = resolveTypeChoice(v);
                    if (next) setForm({ ...form, project_type: next });
                  }}
                  options={buildTypeOptions(form.project_type)} testid="new-type" />
          <Input label="Director/a" value={form.director} onChange={(v) => setForm({ ...form, director: v })} testid="new-director" suggestions={suggestions.directors} />
        </div>
        {SERIES_TYPES.has(form.project_type) && (
          <Input label="Nº de capítulos" value={String(form.episodes || "")}
                 onChange={(v) => setForm({ ...form, episodes: Number(v) || 0 })}
                 testid="new-episodes" />
        )}
        <Select label="Fase actual"
                value={form.phase}
                onChange={(v) => setForm({ ...form, phase: v })}
                options={PROJECT_PHASES}
                testid="new-phase" />
        <Input label="Productora" value={form.producer} onChange={(v) => setForm({ ...form, producer: v })} testid="new-producer" suggestions={suggestions.producers} />
      </div>
      <div className="mt-7 flex justify-end gap-2">
        <button type="button" onClick={onCancel}
                className="rounded-full border border-gb-paper px-5 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-muted hover:bg-gb-paper-soft">
          Cancelar
        </button>
        <button type="submit" data-testid="new-submit"
                className="rounded-full bg-gb-red px-6 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-white transition hover-gb-red">
          Crear proyecto
        </button>
      </div>
    </form>
  </div>
);

const Input = ({ label, value, onChange, required, testid, suggestions }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gb-muted">{label}</span>
    {suggestions ? (
      <AutocompleteInput
        value={value} onChange={onChange}
        options={suggestions}
        required={required}
        data-testid={testid}
        className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-base outline-none focus:border-gb-red focus:ring-2 focus:ring-gb-red/20"
      />
    ) : (
      <input
        type="text" required={required}
        value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-base outline-none focus:border-gb-red focus:ring-2 focus:ring-gb-red/20"
      />
    )}
  </label>
);

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-gb-muted">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="mt-1 w-full rounded-md border border-gb-paper bg-white px-3 py-2.5 text-base outline-none focus:border-gb-red">
      {options.map((o) => <option key={o}>{o}</option>)}
    </select>
  </label>
);
